import { api } from "./client";

// Динамичен breakdown по начин на плащане — смята се при всяко четене (preview,
// списък, детайл); празен е само в отговора на самото `POST` (записът).
export interface PaymentBreakdownItem {
  payment_method_id: number;
  name_bg: string;
  name_en: string;
  is_active: boolean;
  // Decimal сериализиран като string — bills_count може да е дробно (mixed се
  // разпределя пропорционално между Cash и Card).
  amount: string;
  bills_count: string;
  /** „deposit" — редът „Плащане с депозит" (частта от сметките, платена с
   *  депозит); null/липсва за всички останали методи. */
  system_code?: string | null;
}

// Всички Decimal полета от BE идват като JSON НИЗ (виж CLAUDE.md) — парсвай
// явно преди аритметика. Броячите на сметки по метод също са Decimal (смесено
// плащане се разпределя дробно), само total_bills_count е цяло число.
export interface CancelledItem {
  item_id: number;
  item_name: string;
  quantity: string;
  total_amount: string;
}

/** Бакшишите с един метод извън картата (BE `tip_other_breakdown`). */
export interface TipOtherBreakdownRow {
  payment_method_id: number;
  name_bg: string;
  name_en: string | null;
  amount: string;
  bills_count: number;
}

/**
 * Бакшишът зад отчета (docs/reports.md §Бакшиш). ВСИЧКО се смята в BE по
 * сметка — от снимката ѝ при закриването — и после се сумира; FE само
 * показва и сумира тези низове, никога не смята „За отчитане" сам.
 * Optional: по-старо BE ги няма → отчетът е „без бакшиш".
 */
export interface ReportTipFields {
  tip_total_amount?: string;
  tip_card_amount?: string;
  tip_card_deducted_amount?: string;
  tip_card_separate_amount?: string;
  tip_card_expense_amount?: string;
  tip_other_amount?: string;
  tip_other_breakdown?: TipOtherBreakdownRow[] | null;
  /** „За отчитане" = в брой − картовия бакшиш за приспадане. Може да е
   *  ОТРИЦАТЕЛНО (доплащане към персонала). */
  amount_to_report?: string;
  /** Поръчаното + бакшишите — сверка с касовите бонове. */
  total_paid_amount?: string;
  /** С карта общо (поръчано + бакшиш с карта) — сверка с терминала. */
  card_paid_amount?: string;
}

/** Взетите депозити с един метод (BE `deposits_taken_breakdown`). */
export interface DepositTakenBreakdownRow {
  payment_method_id: number;
  name_bg: string;
  name_en: string | null;
  amount: string;
  count: number;
}

/**
 * Взетите депозити зад отчета (BE `reports/service_deposits.py`). Взетите в
 * брой вече са в `amount_to_report`; платеното С депозит е редът „Плащане с
 * депозит" в `payment_breakdown`. Optional: по-старо BE → „без депозити".
 */
export interface ReportDepositFields {
  deposits_taken_amount?: string;
  deposits_taken_cash_amount?: string;
  deposits_taken_breakdown?: DepositTakenBreakdownRow[] | null;
}

export interface Report extends ReportTipFields, ReportDepositFields {
  id: number;
  report_number: number;
  report_type: string;
  user_id: number;
  user_name: string;
  report_date: string;
  created_at: string;
  created_by_user_id: number;
  // Снимка на името към момента на съставяне; null за отчети отпреди колоната.
  created_by_name: string | null;
  total_bills_count: number;
  total_amount: string;
  cash_amount: string;
  cash_bills_count: string;
  card_amount: string;
  card_bills_count: string;
  first_order_time: string | null;
  last_order_time: string | null;
  first_bill_closed: string | null;
  last_bill_closed: string | null;
  cancelled_items_summary: CancelledItem[] | null;
  // Продаденото от сметките зад отчета, по един ред на продаваем. Празен масив
  // за отчетите отпреди чекбокса и за тези, съставени с изключен чекбокс.
  sold_sellables_summary: SoldSellable[];
  payment_breakdown?: PaymentBreakdownItem[];
  // Остатъкът извън кеш/карта — BE-то го смята (`total − cash − card`), затова
  // излиза верен и за отчетите отпреди разбивката по методи.
  other_amount: string;
  other_bills_count: string;
}

export interface ReportListResponse {
  items: Report[];
  total: number;
}

export interface CreateReportRequest {
  user_id: number;
  user_name: string;
  report_date: string;
  // Чекбоксът „Покажи продадените асортименти/рецепти" в модала за генериране.
  // По подразбиране изключен — тогава BE-то изобщо не смята и не записва
  // секцията, тоест отчетът я няма и в Справки, и на POS билета.
  include_sold_sellables?: boolean;
}

export interface ReportFilters {
  user_id?: number;
  date_from?: string;
  date_to?: string;
}

export interface StationBreakdown {
  station_id: number;
  station_name: string;
  cash_amount: string;
  cash_bills_count: string;
  card_amount: string;
  card_bills_count: string;
  // Платеното по методи извън кеш/карта — „По сметка" (id=4) и персонализираните.
  // 0 за отчетите, чиято разбивка е записана преди полето.
  other_amount: string;
  other_bills_count: string;
  total_amount: string;
  total_bills_count: number;
}

export interface UserBreakdown {
  user_id: number;
  user_name: string;
  reports_count: number;
  total_bills_count: number;
  cash_amount: string;
  card_amount: string;
  // Виж StationBreakdown.other_amount.
  other_amount: string;
  total_amount: string;
  // Само при сервитьор с бакшиш; null/липсва — без бакшиш или JSON отпреди
  // полетата → „За отчитане" е `cash_amount`.
  tip_total_amount?: string | null;
  amount_to_report?: string | null;
  /** Само при сервитьор с взет депозит; иначе null/липсва. */
  deposits_taken_amount?: string | null;
}

export interface CancelledItemsByStation {
  station_id: number;
  station_name: string;
  items: CancelledItem[];
}

// Ред от „Продадени асортименти" в отчета на заведението: всичко продадено от
// сметките зад включените персонални отчети, групирано по ПРОДАВАЕМ. Продаваемият
// е артикул ИЛИ рецепта според режима „със склад" — затова полетата са неутрални,
// а името идва вече резолвнато от BE-то. Продуктът и същият продаваем, продаден
// като добавка, са слети в един ред.
export interface SoldSellable {
  sellable_id: number;
  sellable_name: string;
  quantity: string;
  total_amount: string;
}

// Сервитьор със затворени, но неотчетени сметки — личният му отчет ще бъде
// издаден автоматично при „Нулирай" на отчета на заведението. Връща се само
// от establishment preview; за записани отчети списъкът е празен.
export interface UnreportedUser {
  user_id: number;
  user_name: string;
  unreported_amount: string;
}

export interface EstablishmentReport extends ReportTipFields, ReportDepositFields {
  id: number;
  report_number: number;
  report_type: string;
  report_date: string;
  created_at: string;
  created_by_user_id: number;
  created_by_name: string | null;
  total_personal_reports: number;
  total_bills_count: number;
  total_amount: string;
  cash_amount: string;
  cash_bills_count: string;
  card_amount: string;
  card_bills_count: string;
  first_order_time: string | null;
  last_order_time: string | null;
  first_bill_closed: string | null;
  last_bill_closed: string | null;
  stations_breakdown: StationBreakdown[];
  users_breakdown: UserBreakdown[];
  cancelled_items_by_station: CancelledItemsByStation[];
  // Празен масив за отчетите отпреди колоната `sold_sellables_summary`.
  sold_sellables_summary: SoldSellable[];
  payment_breakdown?: PaymentBreakdownItem[];
  unreported_users?: UnreportedUser[];
  // Виж Report.other_amount.
  other_amount: string;
  other_bills_count: string;
}

export interface EstablishmentReportListResponse {
  items: EstablishmentReport[];
  total: number;
}

export interface CreateEstablishmentReportRequest {
  report_date: string;
  // Чекбоксът „Покажи продадените асортименти/рецепти" в модала за генериране.
  // По подразбиране изключен — тогава BE-то изобщо не смята и не записва
  // секцията, тоест отчетът я няма и в Справки, и на POS билета.
  include_sold_sellables?: boolean;
}

export interface EstablishmentReportFilters {
  date_from?: string;
  date_to?: string;
}

export interface PreviewPersonalReportRequest {
  user_id: number;
  user_name: string;
  report_date: string;
  include_sold_sellables?: boolean;
}

export interface PreviewEstablishmentReportRequest {
  report_date: string;
  include_sold_sellables?: boolean;
}

export interface PreviewReport extends Omit<Report, "id"> {
  id: null;
}

export interface PreviewEstablishmentReport extends Omit<
  EstablishmentReport,
  "id"
> {
  id: null;
}

export const createPersonalReport = async (
  data: CreateReportRequest,
): Promise<Report> => {
  const response = await api.post<Report>("/reports/personal", data);
  return response.data;
};

export const getReports = async (
  filters?: ReportFilters,
): Promise<ReportListResponse> => {
  const params = new URLSearchParams();
  if (filters?.user_id) params.append("user_id", filters.user_id.toString());
  if (filters?.date_from) params.append("date_from", filters.date_from);
  if (filters?.date_to) params.append("date_to", filters.date_to);

  const response = await api.get<ReportListResponse>(
    `/reports${params.toString() ? `?${params.toString()}` : ""}`,
  );
  return response.data;
};

export const getReportById = async (reportId: number): Promise<Report> => {
  const response = await api.get<Report>(`/reports/${reportId}`);
  return response.data;
};

export const createEstablishmentReport = async (
  data: CreateEstablishmentReportRequest,
): Promise<EstablishmentReport> => {
  const response = await api.post<EstablishmentReport>(
    "/reports/establishment",
    data,
  );
  return response.data;
};

export const getEstablishmentReports = async (
  filters?: EstablishmentReportFilters,
): Promise<EstablishmentReportListResponse> => {
  const params = new URLSearchParams();
  if (filters?.date_from) params.append("date_from", filters.date_from);
  if (filters?.date_to) params.append("date_to", filters.date_to);

  const response = await api.get<EstablishmentReportListResponse>(
    `/reports/establishment/list${
      params.toString() ? `?${params.toString()}` : ""
    }`,
  );
  return response.data;
};

export const getEstablishmentReportById = async (
  reportId: number,
): Promise<EstablishmentReport> => {
  const response = await api.get<EstablishmentReport>(
    `/reports/establishment/${reportId}`,
  );
  return response.data;
};

export interface PrintReportResult {
  status: "success" | string;
  result?: Record<string, unknown> | null;
}

export const printPersonalReport = async (
  reportId: number,
): Promise<PrintReportResult> => {
  const response = await api.post<PrintReportResult>(
    `/reports/personal/${reportId}/print`,
  );
  return response.data;
};

export const printEstablishmentReport = async (
  reportId: number,
): Promise<PrintReportResult> => {
  const response = await api.post<PrintReportResult>(
    `/reports/establishment/${reportId}/print`,
  );
  return response.data;
};

// „Печат ПОС" в прегледа преди „Нулирай": отчет още няма, затова се праща
// същото тяло като на прегледа — BE сглобява същия билет и нищо не записва.
export const printPersonalReportPreview = async (
  data: PreviewPersonalReportRequest,
): Promise<PrintReportResult> => {
  const response = await api.post<PrintReportResult>(
    "/reports/personal/preview/print",
    data,
  );
  return response.data;
};

export const printEstablishmentReportPreview = async (
  data: PreviewEstablishmentReportRequest,
): Promise<PrintReportResult> => {
  const response = await api.post<PrintReportResult>(
    "/reports/establishment/preview/print",
    data,
  );
  return response.data;
};

export const previewPersonalReport = async (
  data: PreviewPersonalReportRequest,
): Promise<PreviewReport> => {
  const response = await api.post<PreviewReport>(
    "/reports/personal/preview",
    data,
  );
  return response.data;
};

// „Междинен отчет" в „Опции" на Маркиране (право `interim_report`): прегледът
// на СОБСТВЕНИЯ персонален отчет, без издаване. `code` — ПИН-ът за вход с код
// (без тестовите сметки) или кодът за тестов режим (всички сметки); друг код →
// 403 `INTERIM_WRONG_CODE`.
export const INTERIM_WRONG_CODE = "interim_report_wrong_code";

export const previewInterimReport = async (code: string): Promise<Report> => {
  const response = await api.post<Report>("/reports/personal/interim", { code });
  return response.data;
};

export const previewEstablishmentReport = async (
  data: PreviewEstablishmentReportRequest,
): Promise<PreviewEstablishmentReport> => {
  const response = await api.post<PreviewEstablishmentReport>(
    "/reports/establishment/preview",
    data,
  );
  return response.data;
};

// Вид на реда в справката „Асортименти"/„Рецепти" — 1:1 с BE константите
// (ROW_KIND_PRODUCT/ROW_KIND_ADDON/ROW_KIND_LUNCH в reports/service.py).
// „product" е БЕЗ продадените от обедно меню — те са „lunch".
export type OrderedItemKind = "product" | "addon" | "lunch";

export interface OrderedItem {
  category_id: number | null;
  category_name: string | null;
  station_id: number;
  station_name: string;
  item_id: number;
  name: string;
  count: string;
  avg_price: string;
  total_sum: string;
  // Продадена добавка (не самостоятелен продукт). Един и същ продаваем може
  // да има по един ред от всеки вид — цените им се различават.
  is_addon: boolean;
  // Продаден от обедно меню — отделен ред от същия продукт на основната цена.
  is_lunch?: boolean;
}

export interface OrderedItemsResponse {
  items: OrderedItem[];
  total: number;
  unique_items_count: number;
  // Има ли в периода продажби от обедно меню (независимо от филтъра „Тип")
  has_lunch?: boolean;
  total_quantity: string;
  grand_total: string;
  page: number;
  page_size: number;
}

export type OrderedItemsSortBy =
  | "name"
  | "category_name"
  | "station_name"
  | "count"
  | "avg_price"
  | "total_sum"
  | "is_addon";

export type OrderedItemsSortOrder = "asc" | "desc";

export interface OrderedItemsFilters {
  start_date?: string;
  end_date?: string;
  /** Филтърът „смяна" (`lib/reportShift.ts`); без него — всички смени. */
  shift?: number;
  category_ids?: number[];
  station_ids?: number[];
  // Празно = и продуктите, и добавките (както преди появата на филтъра).
  kinds?: OrderedItemKind[];
  search?: string;
  sort_by?: OrderedItemsSortBy;
  sort_order?: OrderedItemsSortOrder;
  page?: number;
  page_size?: number;
  language_code?: string;
}

export const getOrderedItems = async (
  filters?: OrderedItemsFilters,
): Promise<OrderedItemsResponse> => {
  const params = new URLSearchParams();
  if (filters?.start_date) params.append("start_date", filters.start_date);
  if (filters?.end_date) params.append("end_date", filters.end_date);
  if (filters?.shift) params.append("shift", String(filters.shift));
  filters?.category_ids?.forEach((id) =>
    params.append("category_ids", String(id)),
  );
  filters?.station_ids?.forEach((id) =>
    params.append("station_ids", String(id)),
  );
  filters?.kinds?.forEach((kind) => params.append("kinds", kind));
  if (filters?.search) params.append("search", filters.search);
  if (filters?.sort_by) params.append("sort_by", filters.sort_by);
  if (filters?.sort_order) params.append("sort_order", filters.sort_order);
  if (filters?.page) params.append("page", String(filters.page));
  if (filters?.page_size)
    params.append("page_size", String(filters.page_size));
  if (filters?.language_code)
    params.append("language_code", filters.language_code);

  const response = await api.get<OrderedItemsResponse>(
    `/reports/analytics/ordered-items${params.toString() ? `?${params.toString()}` : ""}`,
  );
  return response.data;
};

// ── Разбивката по ДНИ зад един ред на „Асортименти" ─────────────────────────
// `GET /reports/analytics/ordered-items/by-day` — същият въпрос, на който в
// „По станции" отговаря `StationDaysModal`: кой ден вдигна този артикул или
// тази категория. BE-то смята същите редове като родителската справка, само
// групирани по ден (`app/features/reports/service_ordered_items_by_day.py`),
// затова сборът на дните е точно `total_quantity`/`grand_total` на екрана,
// който е отворил разбивката.
export interface OrderedItemsDay {
  // Локална (софийска) ДАТА във формат „YYYY-MM-DD" — ден, не момент. Ден без
  // продажби изобщо не се връща, тоест масивът може да е с дупки.
  date: string;
  count: string;
  avg_price: string;
  total_sum: string;
}

export interface OrderedItemsByDayResponse {
  // Възходящо по дата.
  days: OrderedItemsDay[];
  total_count: string;
  grand_total: string;
}

export interface OrderedItemsByDayFilters {
  start_date?: string;
  end_date?: string;
  /** Филтърът „смяна" (`lib/reportShift.ts`); без него — всички смени. */
  shift?: number;
  // Стеснението на кликнатия ред. `is_addon` се ПРЕСИЧА с `kinds`, не го
  // заменя — ред-добавка при филтър „само продукти" няма какво да покаже.
  item_id?: number;
  station_id?: number;
  is_addon?: boolean;
  is_lunch?: boolean;
  // Активните филтри на справката, която отваря разбивката — без тях числата
  // на модала няма да се събират до числата на таблицата отдолу.
  category_ids?: number[];
  station_ids?: number[];
  kinds?: OrderedItemKind[];
  language_code?: string;
}

export const getOrderedItemsByDay = async (
  filters?: OrderedItemsByDayFilters,
): Promise<OrderedItemsByDayResponse> => {
  const params = new URLSearchParams();
  if (filters?.start_date) params.append("start_date", filters.start_date);
  if (filters?.end_date) params.append("end_date", filters.end_date);
  if (filters?.shift) params.append("shift", String(filters.shift));
  if (filters?.item_id !== undefined)
    params.append("item_id", String(filters.item_id));
  if (filters?.station_id !== undefined)
    params.append("station_id", String(filters.station_id));
  if (filters?.is_addon !== undefined)
    params.append("is_addon", String(filters.is_addon));
  // Обеден и обикновен ред на един продукт са два реда — без това срезът по
  // дни на всеки от тях събира и двата
  if (filters?.is_lunch !== undefined)
    params.append("is_lunch", String(filters.is_lunch));
  filters?.category_ids?.forEach((id) =>
    params.append("category_ids", String(id)),
  );
  filters?.station_ids?.forEach((id) =>
    params.append("station_ids", String(id)),
  );
  filters?.kinds?.forEach((kind) => params.append("kinds", kind));
  if (filters?.language_code)
    params.append("language_code", filters.language_code);

  const response = await api.get<OrderedItemsByDayResponse>(
    `/reports/analytics/ordered-items/by-day${params.toString() ? `?${params.toString()}` : ""}`,
  );
  return response.data;
};
